// @flow
import forge from 'node-forge'
import config from 'config'
import {format as dateFormat} from 'date-fns'
import cnLocale from 'date-fns/locale/zh_cn'
import frLocale from 'date-fns/locale/fr'
import enLocale from 'date-fns/locale/en'
import I18n from 'I18n'
import {values} from 'lodash'

const I18nLocal = I18n.common

export const actionCreator = (params: {
  actionType: string,
  apiFunction: Function,
  requestPayload: Function,
  apiPayload?: Function,
  successPayload: Function,
  taskAfterSuccess?: Function,
  taskAfterFailure?: Function
}) => {
  const {
    actionType,
    apiFunction,
    requestPayload,
    successPayload,
    taskAfterSuccess,
    taskAfterFailure,
    apiPayload
  } = params
  return (dispatch: *, getState: *) => {
    dispatch({
      type: `${actionType}_REQUEST`,
      payload: requestPayload()
    })
    return apiFunction(...values(apiPayload ? apiPayload() : requestPayload()))
      .then(response => {
        dispatch({
          type: `${actionType}_SUCCESS`,
          payload: successPayload(response)
        })
        if (taskAfterSuccess) {
          taskAfterSuccess(response)
        }
      })
      .catch(error => {
        dispatch({
          type: `${actionType}_FAILURE`,
          payload: {...requestPayload(), error: error}
        })
        if (taskAfterFailure) {
          taskAfterFailure(error)
        }
      })
  }
}

export const formatTimestampToDate = (date: any, format: string) => {
  const lang = localStorage.getItem('lang')
  let locale = enLocale
  if (lang === 'cn') {
    locale = cnLocale
  } else if (lang === 'fr') {
    locale = frLocale
  }
  return dateFormat(date, format, {locale: locale}).replace(/\./g, '')
}

export const timeDifference = (
  current: number,
  previous: number,
  isCheckTimeRemaining?: boolean
) => {
  const msPerSecond = 1000
  const msPerMinute = 60 * msPerSecond
  const msPerHour = msPerMinute * 60
  const msPerDay = msPerHour * 24
  const msPerMonth = msPerDay * 30
  const msPerYear = msPerDay * 365
  const elapsed = !isCheckTimeRemaining
    ? current - previous
    : previous - current
  let result = null

  if (elapsed > 0) {
    if (elapsed < msPerMinute) {
      const num = Math.round(elapsed / msPerSecond)
      result = I18n.formatString(
        isCheckTimeRemaining ? I18nLocal.secondsLeft : I18nLocal.secondsAgo,
        num,
        num > 1 ? 's' : ''
      )
    } else if (elapsed < msPerHour) {
      const num = Math.round(elapsed / msPerMinute)
      result = I18n.formatString(
        isCheckTimeRemaining ? I18nLocal.minutesLeft : I18nLocal.minutesAgo,
        num,
        num > 1 ? 's' : ''
      )
    } else if (elapsed < msPerDay) {
      const num = Math.round(elapsed / msPerHour)
      result = I18n.formatString(
        isCheckTimeRemaining ? I18nLocal.hoursLeft : I18nLocal.hoursAgo,
        num,
        num > 1 ? 's' : ''
      )
    } else if (elapsed < msPerMonth) {
      const num = Math.round(elapsed / msPerDay)
      result = I18n.formatString(
        isCheckTimeRemaining ? I18nLocal.daysLeft : I18nLocal.daysAgo,
        num,
        num > 1 ? 's' : ''
      )
    } else if (elapsed < msPerYear) {
      const num = Math.round(elapsed / msPerMonth)
      result = I18n.formatString(
        isCheckTimeRemaining ? I18nLocal.monthsLeft : I18nLocal.monthsAgo,
        num,
        num > 1 ? 's' : ''
      )
    } else {
      const num = Math.round(elapsed / msPerYear)
      result = I18n.formatString(
        isCheckTimeRemaining ? I18nLocal.yearsLeft : I18nLocal.yearsAgo,
        num,
        num > 1 ? 's' : ''
      )
    }
  }
  return result
}

export const formatPercentNumber = (value: number) => {
  return parseFloat(value.toFixed(2))
}

export const validateEmail = (email: string): boolean => {
  var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

export const getApiNameFromFailureAction = (type: string) => {
  return type
    .replace(/_/g, ' ')
    .replace('FAILURE', '')
    .substring(type.indexOf('/') + 1, type.length)
}

export const checkHaveNoEmptyInArray = (vars: Array<any>) => {
  for (let i = 0; i < vars.length; i++) {
    if (vars[i] && vars[i] !== '' && vars[i] !== 0) {
      return true
    }
  }
  return false
}

// RSA-OAEP unwrap params copied from lxp-web decryptSymmetricKeyNew.
// The server wraps with SHA-256 + MGF1-SHA-1; do not change the MGF1 hash.
const unwrapSymmetricKey = () => {
  const encryptedSymmetricKey = localStorage.getItem('symmetricKeyV1') || ''
  // OAEP-only key transport: never decrypt PKCS#1 leftovers in `symmetricKey`.
  if (!encryptedSymmetricKey) {
    localStorage.removeItem('privateKeyPem')
    throw new Error('device unbound')
  }
  const privateKeyPem = localStorage.getItem('privateKeyPem') || ''
  const privateKey = forge.pki.privateKeyFromPem(privateKeyPem)
  const symmetricKeyText = forge.util.decode64(encryptedSymmetricKey)
  return privateKey.decrypt(symmetricKeyText, 'RSA-OAEP', {
    md: forge.md.sha256.create(),
    mgf1: {
      md: forge.md.sha1.create()
    }
  })
}

export const encryptAES = (body: string) => {
  const ivBytes = crypto.getRandomValues(new Uint8Array(12))
  const iv = String.fromCharCode.apply(null, ivBytes)
  const symmetricKey = unwrapSymmetricKey()
  const cipher = forge.cipher.createCipher('AES-GCM', window.atob(symmetricKey))
  cipher.start({
    iv: iv
  })
  cipher.update(forge.util.createBuffer(body))
  cipher.finish()
  const encrypted = cipher.output
  const tag = cipher.mode.tag
  const buffer = forge.util.createBuffer()
  buffer.putBytes(iv)
  buffer.putBytes(encrypted.data)
  buffer.putBytes(tag.data)

  const encryptedWithTagAndIV = forge.util.encode64(buffer.data)
  return `vault:${config.API_VERSION}:${encryptedWithTagAndIV}`
}

export const decryptAES = (textEncrypted: string) => {
  const symmetricKey = unwrapSymmetricKey()

  const resRemovedPrefix = textEncrypted.replace(
    `vault:${config.API_VERSION}:`,
    ''
  )
  const decodedData = window.atob(resRemovedPrefix)
  const iv = decodedData.slice(0, 12)
  const text = decodedData.slice(12, decodedData.length - 16)
  const tag = decodedData.slice(decodedData.length - 16)

  const decipher = forge.cipher.createDecipher(
    'AES-GCM',
    window.atob(symmetricKey)
  )
  decipher.start({
    iv: iv,
    tagLength: 128,
    tag: tag
  })
  decipher.update(forge.util.createBuffer(text))
  const pass = decipher.finish()
  if (pass) {
    return JSON.parse(decipher.output.data)
  } else {
    throw Error('cannot decrypt data')
  }
}

export const formatNumber = (number: number) => {
  return number.toLocaleString()
}
